import React, { useState, useEffect, useRef } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { useCart } from './CartContext';
import CartItemCard from './CartItemCard';
import OrderReferenceCard from './OrderReferenceCard';
import { useCompany } from '../context/CompanyContext';
import repositoryContainer from '../../services/RepositoryContainer';
import Icon from '../AppIcon';
import { getDisplayedProcessingFee } from '../../utils/feeDisplayUtils';

const pendingKey = (companyPath) => `clawfe:pendingOrder:${companyPath || 'global'}`;

const FeeSkeleton = () => (
  <div className="space-y-2.5 animate-pulse">
    {[1, 2, 3].map((i) => (
      <div key={i} className="flex justify-between">
        <div className="h-3 w-20 bg-muted rounded-full" />
        <div className="h-3 w-16 bg-muted rounded-full" />
      </div>
    ))}
    <div className="border-t border-border pt-3 flex justify-between">
      <div className="h-4 w-14 bg-muted rounded-full" />
      <div className="h-4 w-20 bg-muted rounded-full" />
    </div>
  </div>
);

const CartModal = () => {
  const { items, isOpen, closeCart, clearCart } = useCart();
  const { company, companyPath } = useCompany();
  const requiresPayment = company?.requiresPayment ?? false;

  const [clientName, setClientName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successReference, setSuccessReference] = useState(null);

  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [pendingReferenceCode, setPendingReferenceCode] = useState(null);
  const [pendingOrderDetails, setPendingOrderDetails] = useState(null);
  const [pendingOrderLoading, setPendingOrderLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  const [isRedirecting, setIsRedirecting] = useState(false);
  const [redirectingCode, setRedirectingCode] = useState('');

  const [fees, setFees] = useState(null);
  const [feesLoading, setFeesLoading] = useState(false);
  const feesTimer = useRef(null);

  const resetCartFlowState = () => {
    setSuccessReference(null);
    setIsRedirecting(false);
    setRedirectingCode('');
    setPaymentError('');
  };

  // Restore pending order from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(pendingKey(companyPath));
      if (stored) {
        const { orderId, referenceCode, email } = JSON.parse(stored);
        setPendingOrderId(orderId);
        setPendingReferenceCode(referenceCode);
        setClientEmail(email || '');
      }
    } catch {}
  }, [companyPath]);

  // Fetch pending order details once we have a reference code
  useEffect(() => {
    if (!pendingReferenceCode) return;
    setPendingOrderLoading(true);
    repositoryContainer.order.getByReferenceCode(pendingReferenceCode)
      .then((data) => setPendingOrderDetails(data?.response ?? data))
      .catch(() => setPendingOrderDetails(null))
      .finally(() => setPendingOrderLoading(false));
  }, [pendingReferenceCode]);

  useEffect(() => {
    if (!isOpen) return;
    resetCartFlowState();
  }, [isOpen]);

  // Debounced fee calculation — fires 400ms after cart changes, not on every keystroke
  useEffect(() => {
    if (!isOpen || items.length === 0 || !requiresPayment) {
      setFees(null);
      setFeesLoading(false);
      clearTimeout(feesTimer.current);
      return;
    }

    const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
    setFeesLoading(true);

    clearTimeout(feesTimer.current);
    feesTimer.current = setTimeout(async () => {
      try {
        const result = await repositoryContainer.order.calculateFees(subtotal, companyPath);
        setFees(result?.response ?? result);
      } catch {
        setFees(null);
      } finally {
        setFeesLoading(false);
      }
    }, 400);

    return () => clearTimeout(feesTimer.current);
  }, [items, isOpen, companyPath, requiresPayment]);

  if (!isOpen) return null;

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const displayTotal = requiresPayment ? (fees?.total ?? subtotal) : subtotal;

  const savePendingOrder = (orderId, referenceCode, email, paymentReference = null) => {
    localStorage.setItem(
      pendingKey(companyPath),
      JSON.stringify({ orderId, referenceCode, email, paymentReference })
    );
  };

  const clearPendingOrder = () => {
    localStorage.removeItem(pendingKey(companyPath));
    setPendingOrderId(null);
    setPendingReferenceCode(null);
    setPendingOrderDetails(null);
    setPaymentError('');
  };

  const initiatePaymentForOrder = async (orderId, email, referenceCode) => {
    const callbackUrl = `${window.location.origin}/${companyPath}/order-callback`;
    const raw = await repositoryContainer.order.initiatePayment(orderId, email, callbackUrl);
    const paymentData = raw?.response ?? raw;
    savePendingOrder(orderId, paymentData.sourceReferenceCode || referenceCode, email, paymentData.reference);
    window.location.href = paymentData.authorizationUrl;
  };

  const handleRetryPayment = async () => {
    if (!pendingOrderId) return;
    resetCartFlowState();
    setPaymentError('');
    setIsLoading(true);
    setRedirectingCode(pendingReferenceCode || '');
    setIsRedirecting(true);
    try {
      await initiatePaymentForOrder(pendingOrderId, clientEmail.trim(), pendingReferenceCode);
    } catch (err) {
      setIsRedirecting(false);
      setPaymentError(err.data?.message || 'Payment initiation failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAbandonOrder = () => clearPendingOrder();

  const handleCheckout = async () => {
    if (!clientName.trim()) { setError('Please enter your name'); return; }
    if (!phoneNumber || phoneNumber.length < 10) { setError('Please enter a valid phone number'); return; }
    if (!clientEmail.trim()) { setError('Please enter your email address'); return; }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(clientEmail.trim())) { setError('Please enter a valid email address'); return; }

    setError('');
    resetCartFlowState();
    setIsLoading(true);

    try {
      const orderPayload = {
        clientName: clientName.trim(),
        clientPhone: phoneNumber,
        clientEmail: clientEmail.trim(),
        items: items.map((item) => ({ serviceItemId: item.id, quantity: item.quantity })),
      };

      const raw = await repositoryContainer.order.create(orderPayload);
      const response = raw?.response ?? raw;

      if (requiresPayment) {
        clearCart();
        savePendingOrder(response.id, response.referenceCode, clientEmail.trim());
        setRedirectingCode(response.referenceCode);
        setIsRedirecting(true);
        try {
          await initiatePaymentForOrder(response.id, clientEmail.trim(), response.referenceCode);
        } catch (payErr) {
          setIsRedirecting(false);
          setPendingOrderId(response.id);
          setPendingReferenceCode(response.referenceCode);
          setPaymentError(payErr.data?.message || 'Could not start payment. Retry below or abandon this order.');
        }
        return;
      }

      setSuccessReference(response.referenceCode);
      clearCart();
      setClientName('');
      setPhoneNumber('');
      setClientEmail('');
    } catch (err) {
      setError(err.data?.message || 'Failed to place order. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    resetCartFlowState();
    closeCart();
  };

  const hasPendingOrder = Boolean(pendingOrderId);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-[var(--color-background)]/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-xl max-h-[92vh] flex flex-col">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
              <Icon name="ShoppingCart" size={17} color="white" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-base text-foreground leading-tight">
                {isRedirecting ? 'Redirecting…' : successReference ? 'Order Confirmed' : hasPendingOrder ? 'Complete Payment' : 'Your Cart'}
              </h2>
              {!successReference && !hasPendingOrder && !isRedirecting && items.length > 0 && (
                <p className="text-xs text-muted-foreground">{itemCount} item{itemCount !== 1 ? 's' : ''}</p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isRedirecting}
            className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Icon name="X" size={15} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">

          {/* Redirecting to payment */}
          {isRedirecting ? (
            <div className="flex flex-col items-center justify-center py-16 px-5 text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-5">
                <Icon name="Loader2" size={32} className="text-primary animate-spin" />
              </div>
              <h3 className="font-heading font-bold text-lg text-foreground mb-1">Redirecting to payment…</h3>
              <p className="text-sm text-muted-foreground mb-5">You're being sent to our secure payment page.</p>
              {redirectingCode && (
                <div className="bg-muted/50 border border-border rounded-xl px-5 py-3 mb-5">
                  <p className="text-xs text-muted-foreground mb-0.5">Order Reference</p>
                  <p className="font-mono text-sm font-bold text-primary">{redirectingCode}</p>
                </div>
              )}
              <p className="text-xs text-muted-foreground">Please don't close this window</p>
            </div>

          /* Success */
          ) : successReference ? (
            <div className="p-5">
              <OrderReferenceCard confirmationNumber={successReference} onClose={handleClose} />
            </div>

          /* Pending payment */
          ) : hasPendingOrder ? (
            <div className="p-5 space-y-4">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
                <Icon name="AlertCircle" size={17} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Unpaid order pending</p>
                  <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                    Your order was placed but payment wasn't completed. Retry or abandon to start a new order.
                  </p>
                </div>
              </div>

              {pendingOrderLoading ? (
                <div className="space-y-2">
                  <div className="h-16 bg-muted animate-pulse rounded-xl" />
                  <div className="h-10 bg-muted animate-pulse rounded-xl" />
                </div>
              ) : pendingOrderDetails && (
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-muted/40 border-b border-border">
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {new Date(pendingOrderDetails.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                      <p className="text-sm font-semibold text-foreground">{pendingOrderDetails.clientName}</p>
                    </div>
                    <span className="font-mono text-xs font-bold text-primary bg-primary/10 px-2.5 py-1 rounded-lg">
                      {pendingOrderDetails.referenceCode}
                    </span>
                  </div>
                  <ul className="divide-y divide-border">
                    {(pendingOrderDetails.items || []).map((item, i) => (
                      <li key={i} className="flex items-center justify-between px-4 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                            {item.quantity}
                          </span>
                          <span className="text-sm text-foreground">{item.itemName}</span>
                        </div>
                        <span className="text-sm font-semibold text-foreground">GH¢{Number(item.totalPrice).toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between px-4 py-3 bg-muted/40 border-t border-border">
                    <span className="text-sm font-bold text-foreground">Total</span>
                    <span className="text-base font-bold text-primary">GH¢{Number(pendingOrderDetails.totalAmount).toFixed(2)}</span>
                  </div>
                </div>
              )}

              {paymentError && (
                <p className="text-sm text-destructive">{paymentError}</p>
              )}

              <div className="space-y-2.5">
                <Button variant="default" fullWidth iconName="CreditCard" iconPosition="left" loading={isLoading} onClick={handleRetryPayment}>
                  {isLoading ? 'Connecting to payment…' : 'Retry Payment'}
                </Button>
                <Button variant="outline" fullWidth onClick={handleAbandonOrder} disabled={isLoading}>
                  Abandon & Start New Order
                </Button>
              </div>
            </div>

          /* Empty cart */
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-5 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Icon name="ShoppingCart" size={28} className="text-muted-foreground" />
              </div>
              <p className="font-heading font-semibold text-foreground mb-1">Your cart is empty</p>
              <p className="text-sm text-muted-foreground">Browse and add items to get started</p>
            </div>

          /* Normal cart */
          ) : (
            <div>
              {/* Items list */}
              <div className="px-5 divide-y divide-border/60">
                {items.map((item) => <CartItemCard key={item.id} item={item} />)}
              </div>

              {/* Fee breakdown */}
              <div className="mx-5 mt-2 mb-4 p-4 rounded-xl bg-muted/30 border border-border">
                {feesLoading ? (
                  <FeeSkeleton />
                ) : fees && requiresPayment ? (
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Order total</span>
                      <span className="font-medium text-foreground">GH¢{Number(fees.sourceTotal ?? subtotal).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {String(fees.paymentKind).toLowerCase() === 'deposit'
                          ? `Deposit (${Number(fees.upfrontPaymentPercentage) || 0}%)`
                          : 'Amount due now'}
                      </span>
                      <span className="font-medium text-foreground">GH¢{Number(fees.subtotal).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Processing fee</span>
                      <span className="font-medium text-foreground">GH¢{getDisplayedProcessingFee(fees).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between pt-2.5 border-t border-border">
                      <span className="font-bold text-sm text-foreground">Pay now</span>
                      <span className="font-bold text-lg text-primary">GH¢{Number(fees.total).toFixed(2)}</span>
                    </div>
                    {Number(fees.balanceAfterPayment) > 0 && (
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Balance after payment</span>
                        <span>GH¢{Number(fees.balanceAfterPayment).toFixed(2)}</span>
                      </div>
                    )}
                    {fees.platformFeeWaived && fees.platformFeeWaiverReason && (
                      <p className="text-xs text-success">Processing fee discount: {fees.platformFeeWaiverReason}</p>
                    )}
                  </div>
                ) : (
                  /* Fallback when fee API is unavailable */
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm text-foreground">Total</span>
                    <span className="font-bold text-lg text-primary">GH¢{subtotal.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer: checkout form ── */}
        {!successReference && !hasPendingOrder && !isRedirecting && items.length > 0 && (
          <div className="border-t border-border px-5 pt-4 pb-5 space-y-3 flex-shrink-0 bg-card">
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Full Name"
                type="text"
                placeholder="John Doe"
                value={clientName}
                onChange={(e) => { setClientName(e.target.value); if (error.includes('name')) setError(''); }}
                error={error.includes('name') ? error : ''}
              />
              <Input
                label="Phone"
                type="tel"
                placeholder="0551234567"
                value={phoneNumber}
                onChange={(e) => { setPhoneNumber(e.target.value); if (error.includes('phone')) setError(''); }}
                error={error.includes('phone') ? error : ''}
              />
            </div>
            <Input
              label="Email Address"
              type="email"
              placeholder="you@email.com"
              value={clientEmail}
              onChange={(e) => { setClientEmail(e.target.value); if (error.includes('mail')) setError(''); }}
              error={error.includes('mail') ? error : ''}
            />
            {error && !error.includes('name') && !error.includes('phone') && !error.includes('mail') && (
              <p className="text-sm text-destructive">{error}</p>
            )}
            <Button
              onClick={handleCheckout}
              variant="default"
              fullWidth
              iconName={requiresPayment ? 'CreditCard' : 'ShoppingBag'}
              iconPosition="left"
              disabled={isLoading || feesLoading}
              loading={isLoading}
            >
              {isLoading
                ? 'Processing…'
                : requiresPayment
                  ? `Pay GH¢${Number(displayTotal).toFixed(2)}`
                  : 'Place Order'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartModal;
